import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddLeasedAssets from "../../Masters/Scope3/LeasedAssets/AddLeasedAssets";

const EXCEL_ASSET_ID_KEY = "Asset ID";

const getEnteredAssetId = (row) =>
  row?.assetId ||
  row?.assetID ||
  row?.equipment ||
  row?.["Asset ID"] ||
  row?.["AssetID"] ||
  row?.["assetId"] ||
  row?.["assetID"] ||
  "";

const getMatchedFlag = (row) => {
  if (!row) return false;
  if (typeof row.equipmentMatched === "boolean") return row.equipmentMatched;
  if (typeof row.assetMatched === "boolean") return row.assetMatched;
  if (typeof row.assetIdMatched === "boolean") return row.assetIdMatched;
  if (typeof row.assetIDMatched === "boolean") return row.assetIDMatched;
  if (typeof row.matched === "boolean") return row.matched;
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  return matchedKey ? Boolean(row[matchedKey]) : false;
};

const setMatchedFlag = (row, next) => {
  if (!row) return row;
  if (typeof row.equipmentMatched === "boolean")
    return { ...row, equipmentMatched: next };
  if (typeof row.assetMatched === "boolean") return { ...row, assetMatched: next };
  if (typeof row.assetIdMatched === "boolean") return { ...row, assetIdMatched: next };
  if (typeof row.assetIDMatched === "boolean") return { ...row, assetIDMatched: next };
  if (typeof row.matched === "boolean") return { ...row, matched: next };
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  if (matchedKey) return { ...row, [matchedKey]: next };
  return { ...row, matched: next };
};

const BulkUploadReview = ({
  matchData = [],
  setMatchData,
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  leasedType = "upstream",
  bulkUploadUrl,
}) => {
  const [masterAssets, setMasterAssets] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);

  // Add-new master drawer (reuse Masters form)
  const [addMasterOpen, setAddMasterOpen] = useState(false);
  const [addRowIndex, setAddRowIndex] = useState(null);
  const [addMasterElement, setAddMasterElement] = useState(null);
  const [pendingCreatedAssetId, setPendingCreatedAssetId] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  const masterFormInputData = useMemo(
    () => [
      { fieldName: "assetId", title: "Asset ID", placeholder: "e.g. LA-001" },
      { fieldName: "locationCity", title: "Location (City)", placeholder: "City" },
      {
        fieldName: "locationCountry",
        title: "Country",
        placeholder: "Tap to Select",
        type: "select",
      },
      {
        fieldName: "typeOfAsset",
        title: "Type of Asset",
        placeholder: "Tap to Select",
        type: "select",
      },
      { fieldName: "leaseStartDate", title: "Lease start date" },
      { fieldName: "leaseEndDate", title: "Lease end date" },
      { fieldName: "assetSize", title: "Asset size", placeholder: "Numeric size" },
      { fieldName: "assetSizeUnit", title: "Asset size unit" },
    ],
    []
  );

  // Document upload state (same UX as other bulk review screens)
  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none"); // none | same | individual
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) {
      setFinalJson(formDataBulkUpload);
    }
  }, [formDataBulkUpload]);

  useEffect(() => {
    const fetchMasters = async () => {
      setLoadingMasters(true);
      try {
        const queryParams = new URLSearchParams({
          leasedType,
          limit: "1000",
          page: "1",
        }).toString();
        const res = await get(`/master/leasedAssets?${queryParams}`);
        setMasterAssets(res?.data?.data || []);
      } catch (e) {
        setMasterAssets([]);
      } finally {
        setLoadingMasters(false);
      }
    };

    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable, leasedType]);

  const assetIdOptions = useMemo(() => {
    const ids = (masterAssets || [])
      .map((a) => a?.assetId)
      .filter((v) => typeof v === "string" && v.trim().length > 0);
    return Array.from(new Set(ids)).sort((a, b) => a.localeCompare(b));
  }, [masterAssets]);

  const getSelectedAssetIdForIndex = (index) => {
    const row = formDataBulkUpload?.[index];
    const selected =
      row?.[EXCEL_ASSET_ID_KEY] || row?.assetId || row?.assetID || "";
    return String(selected || "");
  };

  const setSelectedAssetIdForIndex = (index, nextValue) => {
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      arr[index] = {
        ...existing,
        [EXCEL_ASSET_ID_KEY]: nextValue,
      };
      return arr;
    });
  };

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      const matched = getMatchedFlag(row);
      if (matched) return false;
      const selected = getSelectedAssetIdForIndex(idx);
      return !selected || selected === "add_new";
    });
  }, [matchData, formDataBulkUpload]);

  const openAddMasterDrawer = (index) => {
    const entered = String(getEnteredAssetId(matchData?.[index]) || "").trim();
    setAddRowIndex(index);
    setPendingCreatedAssetId(entered);
    setAddMasterElement({
      assetId: entered,
      locationCity: "",
      locationCountry: "",
      typeOfAsset: "",
      leaseStartDate: "",
      leaseEndDate: "",
      assetSize: "",
      assetSizeUnit: "",
    });
    setAddMasterOpen(true);
    setExpandedRows((prev) => ({ ...prev, [index]: true }));
  };

  const refreshMasterAssetsAndMarkMatched = async () => {
    // Called after master create succeeds (AddLeasedAssets already showed toast)
    try {
      const queryParams = new URLSearchParams({
        leasedType,
        limit: "1000",
        page: "1",
      }).toString();
      const res = await get(`/master/leasedAssets?${queryParams}`);
      const list = res?.data?.data || [];
      setMasterAssets(list);

      if (typeof addRowIndex === "number" && pendingCreatedAssetId) {
        setSelectedAssetIdForIndex(addRowIndex, pendingCreatedAssetId);
        setMatchData((prev) =>
          (prev || []).map((r, i) =>
            i === addRowIndex ? setMatchedFlag(r, true) : r
          )
        );
      }
    } finally {
      setExpandedRows((prev) => ({ ...prev, ...(addRowIndex !== null ? { [addRowIndex]: false } : {}) }));
      setAddRowIndex(null);
      setPendingCreatedAssetId("");
      setAddMasterElement(null);
    }
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) =>
        Array.isArray(prev)
          ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
          : prev
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({
            ...entry,
            "Document (optional)": files,
          }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry) => ({
        ...entry,
        "Document (optional)": files,
      }));
      setFinalJson(updated);
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };
  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({
            ...entry,
            "Document (optional)": null,
          }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry) => ({
        ...entry,
        "Document (optional)": null,
      }));
      setFinalJson(updated);
    }
  };
  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
            i === index ? { ...entry, "Document (optional)": files } : entry
          )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": files } : entry
      );
      setFinalJson(updated);
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };
  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
            i === index ? { ...entry, "Document (optional)": null } : entry
          )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      const updated = finalJson.map((entry, i) =>
        i === index ? { ...entry, "Document (optional)": null } : entry
      );
      setFinalJson(updated);
    }
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    if (documentUploadConfirmed && documentUploadType !== "none") {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
      }
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      const dataToUse =
        Array.isArray(finalJson) && finalJson.length > 0
          ? finalJson
          : formDataBulkUpload;
      const uploadData = {
        excelData: dataToUse,
        documentUploadType,
        commonDocument,
        individualDocuments,
      };
      const res = await post(bulkUploadUrl, uploadData);
      setsnackOpen?.({
        open: true,
        message: res?.data?.message || "Uploaded successfully",
        severity: "success",
      });
      onConfirm?.();
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details
        ?.map((el) => el?.error)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message: errMessage || e?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review the matched and unmatched leased assets data. Add unmatched
            items individually.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Data Entered
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Master Data
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                    Action
                  </TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                      Document
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Asset ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Asset ID</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold" }}></TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchData || []).map((row, index) => {
                  const entered = getEnteredAssetId(row);
                  const matched = getMatchedFlag(row);
                  return (
                    <React.Fragment key={index}>
                      <TableRow>
                        <TableCell>{entered || "-"}</TableCell>
                        <TableCell>
                          {matched ? (
                            <Chip label="Matched" color="success" size="small" />
                          ) : (
                            <FormControl size="small" sx={{ minWidth: "14rem" }}>
                              <Select
                                value={getSelectedAssetIdForIndex(index) || ""}
                                onChange={(e) => {
                                  const nextValue = e.target.value;
                                  if (nextValue === "add_new") {
                                    setSelectedAssetIdForIndex(index, "add_new");
                                    openAddMasterDrawer(index);
                                    return;
                                  }
                                  setSelectedAssetIdForIndex(index, nextValue);
                                  setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                  setAddMasterOpen(false);
                                  setMatchData((prev) =>
                                    (prev || []).map((r, i) =>
                                      i === index
                                        ? setMatchedFlag(r, Boolean(nextValue))
                                        : r
                                    )
                                  );
                                }}
                                fullWidth
                                displayEmpty
                                disabled={loadingMasters}
                              >
                                <MenuItem value="">Tap to Select</MenuItem>
                                <MenuItem value="add_new">
                                  Add New Leased Asset +
                                </MenuItem>
                                {assetIdOptions.map((id) => (
                                  <MenuItem key={id} value={id}>
                                    {id}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell>
                          {!matched &&
                            (expandedRows[index] ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [index]: false,
                                  }));
                                  setAddMasterOpen(false);
                                }}
                                sx={{ minWidth: "80px" }}
                              >
                                Close
                              </Button>
                            ) : (
                              <Chip
                                label={
                                  getSelectedAssetIdForIndex(index)
                                    ? "Selected"
                                    : "Unmatched"
                                }
                                color={
                                  getSelectedAssetIdForIndex(index)
                                    ? "info"
                                    : "warning"
                                }
                                size="small"
                              />
                            ))}
                        </TableCell>
                        {documentUploadConfirmed &&
                          documentUploadType === "individual" && (
                            <TableCell>
                              <BulkFileUpload
                                handleChange={handleIndividualDocumentChange}
                                handleClear={handleIndividualDocumentClear}
                                filesList={
                                  individualDocuments[index] ||
                                  formDataBulkUpload?.[index]?.["Document (optional)"] ||
                                  finalJson?.[index]?.["Document (optional)"] ||
                                  []
                                }
                                index={index}
                              />
                            </TableCell>
                          )}
                      </TableRow>

                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={
                            documentUploadConfirmed &&
                            documentUploadType === "individual"
                              ? 4
                              : 3
                          }
                        >
                          <Collapse
                            in={Boolean(expandedRows[index]) && addRowIndex === index}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ mt: 2 }}>
                              <AddLeasedAssets
                                id={""}
                                addData={addMasterOpen && addRowIndex === index}
                                setAddData={(next) => {
                                  setAddMasterOpen(next);
                                  if (!next) {
                                    setExpandedRows((prev) => ({
                                      ...prev,
                                      [index]: false,
                                    }));
                                  }
                                }}
                                data={masterAssets}
                                element={addMasterElement}
                                addUrl={"/master/leasedAssets"}
                                updateUrl={"/master/leasedAssets"}
                                fetchData={refreshMasterAssetsAndMarkMatched}
                                setsnackOpen={setsnackOpen}
                                formInputData={masterFormInputData}
                                leasedType={leasedType}
                                embedded={true}
                                embeddedTitle="Add Leased Asset"
                                leaseEndStrictlyAfterStart
                                assetIdReadOnly
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <FormControl component="fieldset">
              <FormLabel component="legend">Do you want to upload document?</FormLabel>
              <RadioGroup
                value={documentUploadConfirmed ? "yes" : "no"}
                onChange={(e) =>
                  handleDocumentUploadConfirmation(e.target.value === "yes")
                }
                row
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            {documentUploadConfirmed && (
              <FormControl component="fieldset" sx={{ mb: 2, mt: 2 }}>
                <FormLabel component="legend">Document Upload Type</FormLabel>
                <RadioGroup
                  value={documentUploadType}
                  onChange={handleDocumentUploadTypeChange}
                  row
                >
                  <FormControlLabel
                    value="same"
                    control={<Radio />}
                    label="Same File for All Records"
                  />
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label="Individual File for Each Record"
                  />
                </RadioGroup>
              </FormControl>
            )}

            {documentUploadConfirmed && documentUploadType === "same" && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload Common Document:
                </Typography>
                <BulkFileUpload
                  handleChange={handleCommonDocumentChange}
                  handleClear={handleCommonDocumentClear}
                  filesList={commonDocument}
                />
              </Box>
            )}
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                border: "1px solid #f44336",
              }}
            >
              <Typography
                variant="h6"
                color="error"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Error:
              </Typography>
              {uploadValidationErrors.map((error, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  color="error"
                  sx={{ mb: 0.5 }}
                >
                  • {error}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            disabled={disableConfirm || uploading}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNoDocumentDialog}
        onClose={() => setShowNoDocumentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">No Documents Uploaded</Typography>
            <IconButton onClick={() => setShowNoDocumentDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have selected to upload documents but haven't uploaded any files.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can still upload documents later if needed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={() => setShowNoDocumentDialog(false)}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
          >
            Upload Documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed Without Documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;

